import { CreditCard } from '@/types';
import { v4 as uuidv4 } from 'uuid';
import { normalizeAmount, normalizeInteger } from '@/utils/formValidation';
import {
  saveCardToSupabase,
  deleteCardFromSupabase,
} from '@/lib/supabase-operations';
import { logger } from '@/utils/logger';

export const createCard = async (
  cardData: Omit<CreditCard, 'id' | 'used' | 'available' | 'createdAt'>,
  planLimits: { maxCards?: number | 'unlimited' },
  currentCardsCount: number
): Promise<CreditCard> => {
  logger.info('Creating card', { name: cardData.name });

  // Enforce subscription limits
  if (typeof planLimits.maxCards === 'number' && currentCardsCount >= planLimits.maxCards) {
    throw new Error(`Seu plano permite no máximo ${planLimits.maxCards} cartão(ões) de crédito. Atualize seu plano para adicionar mais.`);
  }

  // Normalize numeric values
  const normalizedCard = {
    ...cardData,
    limit: normalizeAmount(cardData.limit),
    dueDate: normalizeInteger(cardData.dueDate),
    closingDate: normalizeInteger(cardData.closingDate || 3)
  };

  // Validate required fields
  if (!normalizedCard.name?.trim()) {
    throw new Error('Nome do cartão é obrigatório');
  }
  if (!normalizedCard.bank?.trim()) {
    throw new Error('Banco é obrigatório');
  }
  if (!normalizedCard.accountId?.trim()) {
    throw new Error('Conta vinculada é obrigatória');
  }
  if (normalizedCard.limit <= 0) {
    throw new Error('Limite deve ser maior que zero');
  }
  if (normalizedCard.dueDate < 1 || normalizedCard.dueDate > 31) {
    throw new Error('Data de vencimento deve ser entre 1 e 31');
  }

  const newCard: CreditCard = {
    id: uuidv4(),
    ...normalizedCard,
    used: 0,
    available: normalizedCard.limit,
    createdAt: new Date().toISOString(),
  };

  await saveCardToSupabase(newCard);
  logger.info('Card created successfully', { id: newCard.id });
  
  return newCard;
};

export const updateCardData = async (
  id: string,
  updates: Partial<CreditCard>,
  currentCard: CreditCard
): Promise<CreditCard> => {
  logger.info('Updating card', { id });

  // Normalize numeric values in updates
  const normalizedUpdates = { ...updates };

  if (updates.limit !== undefined) {
    normalizedUpdates.limit = normalizeAmount(updates.limit);
  }
  if (updates.dueDate !== undefined) {
    normalizedUpdates.dueDate = normalizeInteger(updates.dueDate);
  }
  if (updates.closingDate !== undefined) {
    normalizedUpdates.closingDate = normalizeInteger(updates.closingDate);
  }

  const updatedCard = { ...currentCard, ...normalizedUpdates };
  
  await saveCardToSupabase(updatedCard);
  logger.info('Card updated successfully', { id });
  
  return updatedCard;
};

export const deleteCard = async (id: string): Promise<void> => {
  logger.info('Deleting card', { id });
  await deleteCardFromSupabase(id);
  logger.info('Card deleted successfully', { id });
};

export const updateCardUsage = async (
  card: CreditCard,
  amount: number
): Promise<void> => {
  const newUsed = Math.max(0, card.used + amount);
  const updatedCard = { ...card, used: newUsed };
  
  try {
    await saveCardToSupabase(updatedCard);
  } catch (error) {
    logger.error('Error updating card usage', { cardId: card.id, error });
    throw error;
  }
};
